/**
 * SmithVisionAnalyzer — Iter258
 *
 * Claude Vision analyser used inside Smith Protective screens. Accepts
 * either a fresh image upload OR an existing Import Hub asset URL,
 * runs one of 4 presets (or a custom prompt) and renders the analysis.
 *
 * Props:
 *   • prospectId — optional; when set the analysis is filed on the
 *                     prospect's timeline automatically
 *   • defaultImageUrl — optional; pre-fills an asset URL from Import Hub
 *   • onAnalysisDone — callback (analysis) => void when the LLM returns
 *
 * The endpoint is non-streaming (SDK build limitation) — expect a 10-25s
 * wait; we show a determinate progress bar animation.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Eye, Upload, Sparkles, Loader2, X, ImageIcon, ClipboardCopy,
  ScanEye, Building2, DoorOpen, ClipboardList, MailPlus, Images,
  Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT_EXT = [".png", ".jpg", ".jpeg", ".webp"];
const PRESET_ICON = {
  security_gap: ScanEye,
  floor_plan:   Building2,
  gate_audit:   DoorOpen,
  site_audit:   Images,    // Iter259 · multi-image batch
  custom:       ClipboardList,
};


export default function SmithVisionAnalyzer({
  prospectId,
  defaultImageUrl = "",
  onAnalysisDone,
}) {
  const navigate = useNavigate();
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState("security_gap");
  const [customPrompt, setCustomPrompt] = useState("");
  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(defaultImageUrl);
  const [preview, setPreview] = useState(defaultImageUrl || null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);       // { body, duration_ms, ... }
  const [history, setHistory] = useState([]);
  const [openHistoryId, setOpenHistoryId] = useState(null);
  // Iter259 · Multi-image batch
  const [batchFiles, setBatchFiles] = useState([]);           // File[]
  const [batchPreviews, setBatchPreviews] = useState([]);     // [{url, label}]
  const [batchLabels, setBatchLabels] = useState([]);
  // Iter259 · Vision → Outreach bridge
  const [bridging, setBridging] = useState(false);
  const [bridgeResult, setBridgeResult] = useState(null);      // {subject, body, template_id}
  const inputRef = useRef(null);
  const batchInputRef = useRef(null);
  const abortRef = useRef(null);
  const isBatchMode = preset === "site_audit";

  // Load presets + history once.
  useEffect(() => {
    axios.get(`${API}/smith/vision/presets`)
      .then((r) => setPresets(r.data.presets || []))
      .catch(() => { /* silent — dropdown just stays empty */ });
    const params = prospectId ? { prospect_id: prospectId, limit: 20 } : { limit: 20 };
    axios.get(`${API}/smith/vision/history`, { params })
      .then((r) => setHistory(r.data.items || []))
      .catch(() => {});
  }, [prospectId]);

  const currentPresetLabel = useMemo(() =>
    presets.find((p) => p.key === preset)?.label || preset,
    [presets, preset]);

  const pickFile = (f) => {
    if (!f) return;
    if (!ACCEPT_EXT.some((e) => f.name.toLowerCase().endsWith(e))) {
      toast.error(`Formato não suportado — use ${ACCEPT_EXT.join(", ")}`);
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Imagem muito grande (>8MB)");
      return;
    }
    setFile(f); setImageUrl("");
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    if (isBatchMode) {
      const files = Array.from(e.dataTransfer.files || []).slice(0, 5);
      pickBatchFiles(files);
    } else {
      pickFile(e.dataTransfer.files?.[0]);
    }
  };

  // Iter259 · Batch mode helpers
  const pickBatchFiles = (files) => {
    if (!files?.length) return;
    const valid = files.filter((f) =>
      ACCEPT_EXT.some((e) => f.name.toLowerCase().endsWith(e))
       && f.size <= 8 * 1024 * 1024);
    if (valid.length !== files.length) {
      toast.warning(`${files.length - valid.length} arquivo(s) ignorado(s) — extensão ou tamanho`);
    }
    const capped = valid.slice(0, 5);
    if (capped.length < 2) {
      toast.error("Batch precisa de 2 a 5 imagens");
      return;
    }
    setBatchFiles(capped);
    const previews = [];
    const labels = ["frente", "lateral", "fundo", "gate", "planta"].slice(0, capped.length);
    setBatchLabels(labels);
    capped.forEach((f, idx) => {
      const reader = new FileReader();
      reader.onload = () => {
        previews[idx] = { url: reader.result, name: f.name };
        setBatchPreviews([...previews]);
      };
      reader.readAsDataURL(f);
    });
  };
  const removeBatchImage = (idx) => {
    setBatchFiles((cur) => cur.filter((_, i) => i !== idx));
    setBatchPreviews((cur) => cur.filter((_, i) => i !== idx));
    setBatchLabels((cur) => cur.filter((_, i) => i !== idx));
  };
  const updateBatchLabel = (idx, val) => {
    setBatchLabels((cur) => cur.map((l, i) => i === idx ? val.slice(0, 24) : l));
  };

  const run = async () => {
    if (isBatchMode) {
      if (batchFiles.length < 2) {
        toast.error("Envie 2-5 imagens para o Site Audit");
        return;
      }
    } else {
      if (!file && !imageUrl) {
        toast.error("Envie uma imagem OU cole a URL de um asset já importado");
        return;
      }
    }
    if (preset === "custom" && customPrompt.trim().length < 5) {
      toast.error("Preset custom exige um prompt de pelo menos 5 caracteres");
      return;
    }
    setLoading(true); setResult(null); setBridgeResult(null);
    abortRef.current = new AbortController();
    try {
      const fd = new FormData();
      fd.append("preset", preset);
      if (preset === "custom") fd.append("prompt", customPrompt.trim());
      if (prospectId) fd.append("prospect_id", prospectId);

      let endpoint;
      if (isBatchMode) {
        endpoint = `${API}/smith/vision/analyze-batch`;
        fd.append("labels", batchLabels.join(","));
        batchFiles.forEach((f) => fd.append("image_files", f));
      } else {
        endpoint = `${API}/smith/vision/analyze`;
        if (file) fd.append("image_file", file);
        else if (imageUrl) fd.append("image_url", imageUrl);
      }

      const { data } = await axios.post(endpoint, fd, {
        signal: abortRef.current.signal,
        timeout: 95000,
      });
      setResult(data);
      const params = prospectId ? { prospect_id: prospectId, limit: 20 } : { limit: 20 };
      axios.get(`${API}/smith/vision/history`, { params })
        .then((r) => setHistory(r.data.items || []))
        .catch(() => {});
      onAnalysisDone?.(data);
      toast.success(`Claude analisou em ${((data.duration_ms || 0) / 1000).toFixed(1)}s`);
    } catch (e) {
      if (e.name === "CanceledError" || e.code === "ERR_CANCELED") return;
      toast.error(e?.response?.data?.detail || "Falha na análise Claude");
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const cancel = () => abortRef.current?.abort();

  const copyBody = () => {
    if (!result?.body) return;
    navigator.clipboard.writeText(result.body);
    toast.success("Análise copiada");
  };

  // Iter259 · Vision → Outreach bridge
  const bridgeToOutreach = async ({ saveTemplate }) => {
    if (!result?.id) {
      toast.error("Rode uma análise primeiro");
      return;
    }
    setBridging(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/vision/${result.id}/to-outreach`,
        null,
        { params: { save_as_template: saveTemplate },
           timeout: 45000 });
      setBridgeResult(data);
      toast.success(saveTemplate
        ? `Email + template salvo (${data.template_id?.slice(0, 8)})`
        : `Email gerado (${(data.body || "").length} chars)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar email");
    } finally { setBridging(false); }
  };

  const copyBridge = () => {
    if (!bridgeResult?.body) return;
    const full = `Assunto: ${bridgeResult.subject}\n\n${bridgeResult.body}`;
    navigator.clipboard.writeText(full);
    toast.success("Email + assunto copiados");
  };

  const openHistoryItem = async (id) => {
    if (openHistoryId === id) { setOpenHistoryId(null); return; }
    try {
      const { data } = await axios.get(`${API}/smith/vision/history/${id}`);
      setOpenHistoryId(id);
      setResult(data);
      setPreview(null);
    } catch (e) { toast.error("Falha ao carregar análise"); }
  };

  return (
    <div className="rounded-2xl border p-4 space-y-4"
         style={{ background: "rgba(15,23,42,0.55)",
                    borderColor: "rgba(220,38,38,0.28)" }}
         data-testid="smith-vision-analyzer">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#4C1D95] flex items-center justify-center">
          <Eye className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="text-[13.5px] font-black text-white">Claude Vision · Análise de imagem</div>
          <div className="text-[11px] text-slate-400">
            Powered by <span className="text-purple-300 font-bold">claude-sonnet-4-6</span> · presets Smith Protective
          </div>
        </div>
      </div>

      {/* Preset selector */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2"
           data-testid="vision-preset-grid">
        {presets.map((p) => {
          const Icon = PRESET_ICON[p.key] || Sparkles;
          const active = p.key === preset;
          return (
            <button key={p.key} type="button"
                    data-testid={`vision-preset-${p.key}`}
                    onClick={() => setPreset(p.key)}
                    title={p.hint}
                    className={`rounded-lg border p-2 text-left transition ${active ? "border-[#DC2626] bg-red-500/10" : "border-white/10 hover:border-white/30"}`}>
              <div className="flex items-center gap-1.5 mb-0.5">
                <Icon className={`w-3.5 h-3.5 ${active ? "text-[#EF4444]" : "text-slate-400"}`} />
                <div className="text-[11.5px] font-black text-white truncate">
                  {p.label}
                </div>
              </div>
              <div className="text-[9.5px] text-slate-500 line-clamp-2">{p.hint}</div>
            </button>
          );
        })}
      </div>

      {preset === "custom" && (
        <textarea value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  data-testid="vision-custom-prompt"
                  placeholder="Ex: 'Descreva a qualidade da iluminação e onde uma câmera PTZ deveria ficar'"
                  rows={3} maxLength={2000}
                  className="w-full rounded-lg bg-white/5 border border-white/10 text-[12px] text-white p-2.5" />
      )}

      {/* Upload / URL / preview — SINGLE MODE */}
      {!isBatchMode && !preview && (
        <div
          data-testid="vision-drop-zone"
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button" tabIndex={0}
          className={`rounded-xl border-2 border-dashed p-6 flex flex-col items-center gap-2 cursor-pointer transition ${dragging ? "border-[#DC2626] bg-red-500/10" : "border-white/15 hover:border-white/30"}`}>
          <Upload className="w-6 h-6 text-slate-400" />
          <div className="text-[12.5px] font-black text-white">
            Arraste uma imagem .png / .jpg / .webp
          </div>
          <div className="text-[10.5px] text-slate-400">
            ou clique · máx 8MB · também aceita URL de asset do Import Hub
          </div>
          <input ref={inputRef} type="file" hidden
                 accept={ACCEPT_EXT.join(",")}
                 data-testid="vision-file-input"
                 onChange={(e) => pickFile(e.target.files?.[0])} />
          <input value={imageUrl}
                 onChange={(e) => {
                   setImageUrl(e.target.value);
                   if (e.target.value) setPreview(e.target.value);
                 }}
                 onClick={(e) => e.stopPropagation()}
                 data-testid="vision-url-input"
                 placeholder="ou cole aqui uma URL de asset"
                 className="mt-1 w-full max-w-[380px] h-8 px-2 rounded bg-white/5 border border-white/10 text-[11px] text-slate-300 placeholder-slate-500" />
        </div>
      )}
      {!isBatchMode && preview && (
        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black/40">
          <img src={preview} alt="preview"
               className="w-full max-h-[240px] object-contain"
               onError={() => { setPreview(null); }} />
          <button type="button" onClick={() => { setPreview(null); setFile(null); setImageUrl(""); }}
                  data-testid="vision-preview-clear"
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Iter259 · BATCH MODE dropzone + grid */}
      {isBatchMode && batchFiles.length === 0 && (
        <div
          data-testid="vision-batch-drop-zone"
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => batchInputRef.current?.click()}
          role="button" tabIndex={0}
          className={`rounded-xl border-2 border-dashed p-6 flex flex-col items-center gap-2 cursor-pointer transition ${dragging ? "border-[#DC2626] bg-red-500/10" : "border-white/15 hover:border-white/30"}`}>
          <Images className="w-6 h-6 text-slate-400" />
          <div className="text-[12.5px] font-black text-white">
            Arraste de 2 a 5 imagens do site
          </div>
          <div className="text-[10.5px] text-slate-400">
            frente + laterais + gate + planta · Claude devolve UM audit consolidado
          </div>
          <input ref={batchInputRef} type="file" hidden multiple
                 accept={ACCEPT_EXT.join(",")}
                 data-testid="vision-batch-input"
                 onChange={(e) => pickBatchFiles(Array.from(e.target.files || []))} />
        </div>
      )}
      {isBatchMode && batchFiles.length > 0 && (
        <div className="space-y-2" data-testid="vision-batch-grid">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {batchPreviews.map((p, idx) => (
              <div key={idx} className="relative rounded-lg overflow-hidden border border-white/10 bg-black/40"
                    data-testid={`vision-batch-img-${idx}`}>
                <img src={p.url} alt={p.name} className="w-full h-24 object-cover" />
                <button type="button" onClick={() => removeBatchImage(idx)}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 hover:bg-black/90 text-white text-[10px] flex items-center justify-center">
                  <X className="w-2.5 h-2.5" />
                </button>
                <input value={batchLabels[idx] || ""}
                       onChange={(e) => updateBatchLabel(idx, e.target.value)}
                       data-testid={`vision-batch-label-${idx}`}
                       placeholder="label"
                       className="w-full h-6 px-1 text-[10px] bg-black/70 border-t border-white/10 text-white placeholder-slate-500" />
              </div>
            ))}
            {batchFiles.length < 5 && (
              <button type="button" onClick={() => batchInputRef.current?.click()}
                      data-testid="vision-batch-add"
                      className="rounded-lg border border-dashed border-white/20 hover:border-white/40 h-[calc(6rem+1.5rem)] flex flex-col items-center justify-center gap-1 text-slate-500">
                <Upload className="w-4 h-4" />
                <div className="text-[10px]">+ adicionar</div>
              </button>
            )}
          </div>
          <div className="text-[10.5px] text-slate-500">
            {batchFiles.length}/5 imagens · edite os labels para que a Claire referencie corretamente ("na foto do gate…")
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button type="button"
                onClick={loading ? cancel : run}
                disabled={isBatchMode ? batchFiles.length < 2 : !preview}
                data-testid="vision-run-btn"
                className="flex-1 h-10 rounded-lg text-[13px] font-black text-white inline-flex items-center justify-center gap-2 disabled:opacity-40"
                style={{ background: loading
                          ? "linear-gradient(135deg,#78350F 0%,#451A03 100%)"
                          : "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
          {loading
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Cancelar análise</>
            : <><Sparkles className="w-4 h-4" /> Analisar com Claude · {currentPresetLabel}</>}
        </button>
      </div>

      {/* Result panel */}
      {result && (
        <div className="rounded-xl border border-purple-500/25 bg-purple-500/5 p-4 space-y-2"
             data-testid="vision-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] font-black uppercase tracking-widest text-purple-300">
              {result.preset_label || "Análise Claude"} · {result.body_chars || result.body?.length || 0} chars
              {result.duration_ms && ` · ${(result.duration_ms/1000).toFixed(1)}s`}
              {result.batch_count && ` · ${result.batch_count} imgs`}
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={copyBody}
                      data-testid="vision-copy-btn"
                      className="text-[10.5px] font-bold text-purple-300 hover:text-white inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-white/5">
                <ClipboardCopy className="w-3 h-3" /> Copiar
              </button>
            </div>
          </div>
          <div className="text-[12.5px] text-slate-100 leading-relaxed whitespace-pre-wrap"
               data-testid="vision-result-body">
            {result.body}
          </div>
          {/* Iter259 · Vision → Outreach bridge */}
          <div className="pt-2 mt-1 border-t border-purple-500/20 flex flex-wrap items-center gap-2">
            <div className="text-[10.5px] text-slate-400 flex-1 min-w-[140px]">
              Transformar essa análise em cold-email:
            </div>
            <button type="button"
                    onClick={() => bridgeToOutreach({ saveTemplate: false })}
                    disabled={bridging}
                    data-testid="vision-bridge-btn"
                    className="h-8 px-3 rounded-lg text-[11px] font-black text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                    style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
              {bridging
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <MailPlus className="w-3 h-3" />}
              Gerar cold-email
            </button>
            <button type="button"
                    onClick={() => bridgeToOutreach({ saveTemplate: true })}
                    disabled={bridging}
                    data-testid="vision-bridge-save-btn"
                    className="h-8 px-3 rounded-lg text-[11px] font-black text-slate-100 border border-white/15 hover:border-white/30 inline-flex items-center gap-1.5 disabled:opacity-40">
              <ClipboardList className="w-3 h-3" /> + salvar como template
            </button>
          </div>
        </div>
      )}

      {/* Iter259 · Bridge result */}
      {bridgeResult && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 space-y-2"
             data-testid="vision-bridge-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] font-black uppercase tracking-widest text-red-300 inline-flex items-center gap-1">
              <MailPlus className="w-3 h-3" /> Cold-email gerado pela Claire
              {bridgeResult.template_id && (
                <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-[9px]">
                  template salvo
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={copyBridge}
                      data-testid="vision-bridge-copy"
                      className="text-[10.5px] font-bold text-red-300 hover:text-white inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-white/5">
                <ClipboardCopy className="w-3 h-3" /> Copiar assunto+corpo
              </button>
              {prospectId && (
                <button type="button"
                        onClick={() => {
                          const params = new URLSearchParams({
                            tab: "retry",
                            prefill_vision: "1",
                            prospect_id: prospectId,
                            analysis_id: bridgeResult.analysis_id || result?.id || "",
                          });
                          navigate(`/smith/outreach?${params.toString()}`);
                        }}
                        data-testid="vision-bridge-to-retry"
                        title="Abrir o Editar & Reenviar do Outreach com este cold-email"
                        className="ml-1 h-7 px-2.5 rounded-lg text-[10.5px] font-black text-white inline-flex items-center gap-1"
                        style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                  <Send className="w-3 h-3" /> Enviar como Retry
                </button>
              )}
            </div>
          </div>
          <div className="text-[11px] text-slate-400">
            <b className="text-white">Assunto:</b> {bridgeResult.subject}
          </div>
          <div className="text-[12.5px] text-slate-100 leading-relaxed whitespace-pre-wrap"
               data-testid="vision-bridge-body">
            {bridgeResult.body}
          </div>
        </div>
      )}

      {/* Recent history */}
      {history.length > 0 && (
        <div className="pt-2 border-t border-white/10">
          <div className="text-[10.5px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
            Análises recentes {prospectId ? "deste prospect" : "(global)"}
          </div>
          <div className="space-y-1 max-h-[180px] overflow-y-auto"
               data-testid="vision-history-list">
            {history.map((h) => {
              const Icon = PRESET_ICON[h.preset] || ImageIcon;
              const active = openHistoryId === h.id;
              return (
                <button key={h.id} type="button"
                        onClick={() => openHistoryItem(h.id)}
                        data-testid={`vision-history-${h.id}`}
                        className={`w-full text-left rounded-md px-2 py-1.5 border ${active ? "bg-white/10 border-white/25" : "border-transparent hover:bg-white/5"}`}>
                  <div className="flex items-start gap-1.5">
                    <Icon className="w-3 h-3 mt-0.5 text-purple-300 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold text-white truncate">
                        {h.preset_label}
                      </div>
                      <div className="text-[10px] text-slate-500 line-clamp-1">
                        {h.preview}
                      </div>
                    </div>
                    <div className="text-[9.5px] text-slate-500 tabular-nums shrink-0">
                      {(h.duration_ms || 0) > 0 && `${((h.duration_ms || 0)/1000).toFixed(1)}s`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
